import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { localize } from 'redux-i18n';
import { withRouter } from 'react-router-dom';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import path from '../../helpers/path';
import NavDashboardIcon from '../../components/PageHeader/icons/NavDashboardIcon';
import NavTradeIcon from '../../components/PageHeader/icons/NavTradeIcon';
import NavWalletIcon from '../../components/PageHeader/icons/NavWalletIcon';
import APLogo from 'apex-web/lib/components/common/APLogo/APLogo';
import APIcon from 'apex-web/lib/components/common/APIcon';
import PageHeaderNavComponent from '../../components/PageHeader/PageHeaderNavComponent';
import UserSummaryContainer from '../../components/UserSummary/UserSummaryContainer';
import './PageHeaderLayout.css';
import './UserSummaryOverrides.css';
import AccountsDropdownContainer from '../../components/AccountsDropdown/AccountsDropdownContainer';
import { isSelectedAccountRetirementSelector } from '../../redux/selectors/accountSelectors';
import config from '../../config';
import { useUiViewMode } from '../../context/UiViewModeContext';
import DashboardViewToggle from '../../components/DashboardViewToggle';

const pageHeaderClasses = getBEMClasses('page-header');

const SWAP_PROMO_FEATURES = ['Low slippage', 'Supporting TSC', 'One-click swap'];

const PageHeaderComponent = ({ children, isSelectedAccountRetirement, t, location }) => {
  const { viewMode, setViewMode } = useUiViewMode();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileChromeScrolled, setMobileChromeScrolled] = useState(false);
  const mainRef = useRef(null);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen(open => !open), []);

  const legalLinks = (config.Footer.links && config.Footer.links[0]) || [];

  const navItems = [
    { text: 'Dashboard', path: '/dashboard', Icon: NavDashboardIcon },
    { text: 'Digital Assets', icon: 'buy-sell', path: '/digital-assets' },
    { text: 'Trade', path: '/trade', Icon: NavTradeIcon }, //AS-23583 Exchange -> Trade
    //{ text: 'Balances', icon: 'balance', path: '/margin-balances' },
    !isSelectedAccountRetirement
      ? { text: 'Streaming', icon: 'activity', path: '/eotc' }
      : null,
    //{ text: 'Lending', icon: 'sell', path: '/iba' },
    { text: 'Buy & Sell', icon: 'buy-sell', path: '/buy-sell' },
    //{ text: 'Convert', icon: 'buy-sell', path: '/convert' },
    { text: 'Wallets', path: '/wallets', Icon: NavWalletIcon },
    legalLinks.length
      ? { key: 'legal', text: 'Legal', icon: 'info', children: legalLinks }
      : null
    // { text: 'Activity', icon: 'activity', path: '/asset-activity-details' }
    // { text: 'Components', icon: 'star', path: '/component' }
  ].filter(Boolean);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useLayoutEffect(() => {
    const el = mainRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return undefined;
    }
    const onKey = e => {
      if (e.key === 'Escape') {
        closeMobileNav();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [mobileNavOpen, closeMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 721px)');
    const onM = () => {
      if (mq.matches) {
        setMobileNavOpen(false);
      }
    };
    mq.addEventListener('change', onM);
    onM();
    return () => mq.removeEventListener('change', onM);
  }, []);

  const updateMobileChromeScrolled = useCallback(() => {
    const mqNarrow = window.matchMedia('(max-width: 720px)').matches;
    if (!mqNarrow) {
      setMobileChromeScrolled(false);
      return;
    }
    const mainEl = mainRef.current;
    const y = Math.max(
      mainEl?.scrollTop ?? 0,
      window.pageYOffset,
      document.documentElement?.scrollTop ?? 0,
      document.body?.scrollTop ?? 0
    );
    setMobileChromeScrolled(y > 6);
  }, []);

  useEffect(() => {
    const mainEl = mainRef.current;
    const mqNarrow = window.matchMedia('(max-width: 720px)');

    const onScroll = () => updateMobileChromeScrolled();
    const onM = () => updateMobileChromeScrolled();

    updateMobileChromeScrolled();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    if (mainEl) {
      mainEl.addEventListener('scroll', onScroll, { passive: true });
    }
    mqNarrow.addEventListener('change', onM);

    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (mainEl) {
        mainEl.removeEventListener('scroll', onScroll);
      }
      mqNarrow.removeEventListener('change', onM);
    };
  }, [updateMobileChromeScrolled]);

  useEffect(() => {
    updateMobileChromeScrolled();
  }, [location.pathname, updateMobileChromeScrolled]);

  const isMainDashboardRoute = location.pathname === '/dashboard';
  const shellClass = `${pageHeaderClasses('shell')}${
    mobileNavOpen ? ' page-header__shell--nav-open' : ''
  }${isMainDashboardRoute ? ' page-header__shell--main-dashboard' : ''}`;

  const mobileChromeClass = pageHeaderClasses('mobile-chrome', {
    scrolled: mobileChromeScrolled
  });

  const siteLogoFile =
    (config.global && config.global.siteLogo) || 't7xnew.png';

  return (
    <div className={shellClass}>
      <div className={mobileChromeClass}>
        <button
          type="button"
          className={pageHeaderClasses('nav-toggle')}
          onClick={toggleMobileNav}
          aria-expanded={mobileNavOpen}
          aria-controls="page-header-sidebar"
          aria-label={mobileNavOpen ? t('Close menu') : t('Open menu')}>
          <APIcon
            name={mobileNavOpen ? 'close' : 'menu-toggle'}
            customClass={pageHeaderClasses('nav-toggle-icon')}
          />
        </button>
        <a
          href="/"
          className={pageHeaderClasses('mobile-brand')}
          aria-label={t('Home')}>
          <img
            className={pageHeaderClasses('mobile-brand-img')}
            src={path(`/local/logos/${siteLogoFile}`)}
            alt=""
            decoding="async"
          />
        </a>
      </div>
      <div
        className={pageHeaderClasses('sidebar-backdrop')}
        onClick={closeMobileNav}
        role="presentation"
        aria-hidden="true"
      />
      <aside
        className={pageHeaderClasses('sidebar')}
        id="page-header-sidebar"
        aria-label={t('Application navigation')}>
        <div className={pageHeaderClasses('sidebar-top')}>
          <div className={pageHeaderClasses('brand')}>
            <APLogo customClass={pageHeaderClasses('logo')} linkTo="/" />
          </div>
          <PageHeaderNavComponent
            navItems={navItems}
            sidebar
            onSidebarItemClick={closeMobileNav}
          />
        </div>
        <div className={pageHeaderClasses('sidebar-view-mode')}>
          <DashboardViewToggle
            value={viewMode}
            onChange={setViewMode}
            t={t}
            label={t('Layout')}
          />
        </div>
        <section
          className={pageHeaderClasses('sidebar-promo')}
          aria-labelledby="sidebar-swap-promo-heading">
          <div className={pageHeaderClasses('sidebar-promo-inner')}>
            <div className={pageHeaderClasses('sidebar-promo-copy')}>
              <h2
                id="sidebar-swap-promo-heading"
                className={pageHeaderClasses('sidebar-promo-title')}>
                {t('Fast, simple swaps')}
              </h2>
              <ul className={pageHeaderClasses('sidebar-promo-list')}>
                {SWAP_PROMO_FEATURES.map(text => (
                  <li key={text}>{t(text)}</li>
                ))}
              </ul>
            </div>
            <div className={pageHeaderClasses('sidebar-promo-visual')}>
              <img
                className={pageHeaderClasses('sidebar-promo-art')}
                src={path('/local/landing-page/images/tsc-usdc.png')}
                alt=""
              />
            </div>
          </div>
        </section>
        <div className={pageHeaderClasses('sidebar-footer')}>
          <UserSummaryContainer
            customClass="page-header-user-summary"
            settingsRoute="/settings/user"
          />
          <AccountsDropdownContainer customClass="page-header-user-summary" />
        </div>
      </aside>
      <div className={pageHeaderClasses('main')} ref={mainRef}>
        {children}
      </div>
    </div>
  );
};

PageHeaderComponent.propTypes = {
  children: PropTypes.node,
  isSelectedAccountRetirement: PropTypes.bool,
  location: PropTypes.shape({ pathname: PropTypes.string.isRequired })
    .isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  isSelectedAccountRetirement: isSelectedAccountRetirementSelector(state)
});

export default connect(
  mapStateToProps
)(localize()(withRouter(PageHeaderComponent)));
